<template>
  <div
    class="waiting-room"
    :class="{
      'with-nav': !gameStarted,
      'game-playing': gameStarted
    }"
    :style="roomBackgroundStyle"
  >
    <!-- Elementos decorativos de fondo tipo Casino -->
    <div class="background-elements">
      <div class="casino-chip chip-1">💰</div>
      <div class="casino-chip chip-2">🎰</div>
      <div class="casino-chip chip-3">🎲</div>
      <div class="casino-chip chip-4">🃏</div>
      <div class="casino-chip chip-5">💎</div>
      <div class="casino-chip chip-6">⭐</div>
      <div class="playing-card card-1">♠</div>
      <div class="playing-card card-2">♥</div>
      <div class="playing-card card-3">♣</div>
      <div class="playing-card card-4">♦</div>
    </div>

    <div class="waiting-container" :class="{ 'game-active': gameStarted }">
      <!-- Selector de mesas (cuando la partida NO ha comenzado) -->
      <template v-if="!gameStarted">
        <div class="exit-section exit-section-top">
          <button type="button" @click="goToDashboard" class="exit-btn">
            <i class="fas fa-arrow-left"></i>
            <span>Inicio</span>
          </button>
        </div>

        <!-- Saldo disponible en vivo -->
        <div class="waiting-balance-bar">
          <div class="waiting-balance-content">
            <span class="waiting-balance-label">
              <i class="fas fa-coins"></i>
              Saldo disponible
            </span>
            <span class="waiting-balance-value">
              {{ formattedUserBalance }}
              <small>Bs</small>
            </span>
          </div>
          <button
            type="button"
            class="waiting-balance-refresh"
            :disabled="isSyncingBalance"
            aria-label="Actualizar saldo"
            title="Actualizar saldo"
            @click="refreshDisplayedBalance"
          >
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': isSyncingBalance }"></i>
          </button>
        </div>

        <!-- Header del jugador con avatar y estado en línea -->
        <div class="player-header">
          <div class="player-info">
            <div class="player-avatar">
              <i class="fas fa-user"></i>
            </div>
            <div class="player-details">
              <h2 class="player-name">{{ playerName }}</h2>
            </div>
          </div>
          <div class="game-status">
            <div class="status-indicator">
              <div class="status-dot"></div>
              <span>En Línea</span>
            </div>
          </div>
        </div>

        <!-- Formulario de selección de mesas -->
        <div class="entry-form">
          <div class="game-modes">
            <h3 class="modes-title">Elige tu Mesa de Juego</h3>
            <p class="modes-subtitle">{{ gameTitle }} · Selecciona donde quieres jugar</p>

            <div class="lobby-hook-wrap">
              <p class="lobby-hook">gana hasta <em>10 mil Bs</em> hoy !</p>
            </div>

            <!-- Grid de mesas estilo Casino -->
            <div class="mode-options">
              <!-- Mesa Principiante (Bot / Práctica) -->
              <div
                class="mode-card cpu-mode"
                @click="selectTable(tables.practice)"
              >
                <div class="mode-badge">POPULAR</div>
                <div class="mode-icon">
                  <i class="fas fa-robot"></i>
                </div>
                <div class="mode-content">
                  <h4>{{ tables.practice.name }}</h4>
                  <p>{{ tables.practice.description }}</p>
                  <div class="mode-pricing">
                    <div class="mode-price mode-prize">
                      <span class="price-label">Recompensa</span>
                      <span class="price-value">
                        <img class="price-coin" src="/img/coins-removebg-preview.png" alt="" width="22" height="22">
                        0 Bs
                      </span>
                    </div>
                    <div class="mode-price mode-entry">
                      <span class="price-label">Entrada</span>
                      <span class="price-value free">GRATIS</span>
                    </div>
                  </div>
                  <div class="mode-features">
                    <span class="feature">🤖 IA Avanzada</span>
                    <span class="feature">⚡ Inicio Rápido</span>
                  </div>
                </div>
                <div class="mode-action">
                  <i class="fas fa-play"></i>
                </div>
              </div>

              <!-- Mesa Duelo 1 vs 1 -->
              <div
                class="mode-card online-mode"
                @click="selectTable(tables.quick1)"
              >
                <div class="mode-badge">MULTIJUGADOR</div>
                <div class="mode-icon">
                  <i class="fas fa-users"></i>
                </div>
                <div class="mode-content">
                  <h4>{{ tables.quick1.name }}</h4>
                  <p>{{ tables.quick1.description }}</p>
                  <div class="mode-pricing">
                    <div class="mode-price mode-prize">
                      <span class="price-label">Recompensa</span>
                      <span class="price-value">
                        <img class="price-coin" src="/img/coins-removebg-preview.png" alt="" width="22" height="22">
                        {{ tables.quick1.payout }} Bs
                      </span>
                    </div>
                    <div class="mode-price mode-entry">
                      <span class="price-label">Entrada</span>
                      <span class="price-value">{{ tables.quick1.entry }} Bs</span>
                    </div>
                  </div>
                  <div class="mode-features">
                    <span class="feature">👥 2 Jugadores</span>
                    <span class="feature">🌐 En Tiempo Real</span>
                  </div>
                </div>
                <div class="mode-action">
                  <i class="fas fa-play"></i>
                </div>
              </div>

              <!-- Mesa Cuadrangular / 4P -->
              <div
                class="mode-card online-mode"
                @click="selectTable(tables.quick4)"
              >
                <div class="mode-badge">TURNO</div>
                <div class="mode-icon">
                  <i class="fas fa-users"></i>
                </div>
                <div class="mode-content">
                  <h4>{{ tables.quick4.name }}</h4>
                  <p>{{ tables.quick4.description }}</p>
                  <div class="mode-pricing">
                    <div class="mode-price mode-prize">
                      <span class="price-label">Recompensa</span>
                      <span class="price-value">
                        <img class="price-coin" src="/img/coins-removebg-preview.png" alt="" width="22" height="22">
                        {{ tables.quick4.payout }} Bs
                      </span>
                    </div>
                    <div class="mode-price mode-entry">
                      <span class="price-label">Entrada</span>
                      <span class="price-value">{{ tables.quick4.entry }} Bs</span>
                    </div>
                  </div>
                  <div class="mode-features">
                    <span class="feature">🎯 4 Jugadores</span>
                    <span class="feature">🔥 Todos vs Todos</span>
                  </div>
                </div>
                <div class="mode-action">
                  <i class="fas fa-play"></i>
                </div>
              </div>

              <!-- Mesa Privada (Código) -->
              <div
                class="mode-card invite-mode"
                @click="openPrivateModal"
              >
                <div class="mode-badge">INVITACIÓN</div>
                <div class="mode-icon">
                  <i class="fas fa-user-plus"></i>
                </div>
                <div class="mode-content">
                  <h4>{{ tables.private.name }}</h4>
                  <p>{{ tables.private.description }}</p>
                  <div class="mode-pricing">
                    <div class="mode-price mode-prize">
                      <span class="price-label">Recompensa</span>
                      <span class="price-value">
                        <img class="price-coin" src="/img/coins-removebg-preview.png" alt="" width="22" height="22">
                        Amistosa
                      </span>
                    </div>
                    <div class="mode-price mode-entry">
                      <span class="price-label">Entrada</span>
                      <span class="price-value free">Código</span>
                    </div>
                  </div>
                  <div class="mode-features">
                    <span class="feature">🔗 Código de Sala</span>
                    <span class="feature">👥 Con Amigos</span>
                  </div>
                </div>
                <div class="mode-action">
                  <i class="fas fa-share-alt"></i>
                </div>
              </div>

              <!-- Panel vinotinto decorativo para completar el grid -->
              <div class="mode-accent-panel" aria-hidden="true">
                <div class="accent-content">
                  <span class="accent-logo">DOMINUES</span>
                  <p class="accent-tagline">Juega. Gana. Domina.</p>
                  <div class="accent-decor">
                    <span>♠</span>
                    <span>♥</span>
                    <span>♣</span>
                    <span>♦</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Viewport de partida activa (reemplaza las mesas) -->
      <div v-else class="game-container">
        <div class="game-header-bar">
          <button type="button" @click="exitToLobby" class="exit-btn">
            <i class="fas fa-arrow-left"></i>
            <span>Volver a Mesas</span>
          </button>
          <div class="game-header-title">
            <span class="game-name-tag">{{ gameTitle }}</span>
            <span class="game-mode-tag">{{ currentModeLabel }}</span>
          </div>
          <div class="game-header-actions">
            <button
              type="button"
              class="sound-toggle-btn"
              @click="toggleSound"
              :title="isMuted ? 'Activar Sonido' : 'Silenciar'"
            >
              <i class="fas" :class="isMuted ? 'fa-volume-mute' : 'fa-volume-up'"></i>
            </button>
          </div>
        </div>

        <div class="game-viewport">
          <iframe
            v-if="gameStarted && launchUrl"
            ref="gameFrame"
            :src="launchUrl"
            frameborder="0"
            class="game-iframe"
            allow="fullscreen; autoplay; screen-wake-lock"
          ></iframe>
        </div>
      </div>
    </div>

    <!-- Modal elegante para Mesa Privada (Crear / Unirse) -->
    <div v-if="showPrivateModal" class="private-modal-backdrop" @click.self="showPrivateModal = false">
      <div class="private-modal-content">
        <div class="private-modal-header">
          <h3>Mesa Privada</h3>
          <button type="button" class="close-modal-btn" @click="showPrivateModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <p class="private-modal-subtitle">Juega con tus amigos creando una sala o ingresando un código.</p>

        <div class="private-tabs">
          <button
            type="button"
            class="tab-btn"
            :class="{ active: privateTab === 'create' }"
            @click="privateTab = 'create'"
          >
            Crear Sala
          </button>
          <button
            type="button"
            class="tab-btn"
            :class="{ active: privateTab === 'join' }"
            @click="privateTab = 'join'"
          >
            Unirme a Sala
          </button>
        </div>

        <div v-if="privateTab === 'create'" class="tab-body">
          <p>Crea una sala personalizada y comparte el código con tus invitados para que jueguen en tiempo real.</p>
          <button type="button" class="action-submit-btn" @click="startPrivateCreate">
            <i class="fas fa-plus-circle"></i>
            <span>Crear y Entrar a la Cancha</span>
          </button>
        </div>

        <div v-else class="tab-body">
          <label class="code-label">CÓDIGO DE SALA</label>
          <input
            v-model="joinCodeInput"
            type="text"
            maxlength="6"
            class="code-input"
            placeholder="ABC123"
            @keyup.enter="startPrivateJoin"
          />
          <button
            type="button"
            class="action-submit-btn"
            :disabled="!isValidJoinCode"
            @click="startPrivateJoin"
          >
            <i class="fas fa-sign-in-alt"></i>
            <span>Unirme a la Partida</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { GAME_CONFIG } from '@/components/waiting/gameConfig'

export default {
  name: 'EmbeddedGameWaitingRoom',
  props: {
    gameType: {
      type: String,
      default: 'bolas'
    }
  },
  data() {
    return {
      gameStarted: false,
      launchUrl: '',
      currentModeLabel: '',
      isSyncingBalance: false,
      isMuted: false,
      showPrivateModal: false,
      privateTab: 'create',
      joinCodeInput: ''
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'currentUser']),
    ...mapGetters('games', ['userBalance']),

    isBolas() {
      return this.gameType === 'bolas' || this.gameType === 'bolas-criollas'
    },

    gameTitle() {
      return this.isBolas ? 'Bolas Criollas' : 'Billar Online'
    },

    baseUrl() {
      if (this.isBolas) {
        return GAME_CONFIG.BOLAS_URL || `${GAME_CONFIG.GAME_URL.replace(/\/+$/, '')}/bolas/`
      }
      return GAME_CONFIG.BILLAR_URL || `${GAME_CONFIG.GAME_URL.replace(/\/+$/, '')}/billar/`
    },

    roomBackgroundStyle() {
      return {
        backgroundImage: `url(${require('@/assets/img/bg3.jpg')})`
      }
    },

    playerName() {
      if (this.currentUser?.name) return this.currentUser.name
      if (this.currentUser?.email) return this.currentUser.email.split('@')[0]
      return 'Usuario'
    },

    formattedUserBalance() {
      const balance = Number(this.userBalance ?? 0)
      return balance.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    tables() {
      if (this.isBolas) {
        return {
          practice: {
            id: 'practice',
            name: 'Mesa Principiante',
            description: 'Mesa para principiantes · vs Bot',
            auto: 'practice',
            label: 'Modo Práctica'
          },
          quick1: {
            id: 'quick1',
            name: 'Mesa Intermedia',
            description: 'Mesa para jugadores intermedios · 1 vs 1',
            entry: 350,
            payout: 630,
            auto: 'quick1',
            label: '1 vs 1 Online'
          },
          quick4: {
            id: 'quick4',
            name: 'Mesa Avanzada',
            description: 'Mesa para expertos · Todos contra todos (4P)',
            entry: 700,
            payout: 2520,
            auto: 'quick4',
            label: '4 Jugadores (FFA)'
          },
          private: {
            id: 'private',
            name: 'Mesa Privada',
            description: 'Mesa privada con código entre amigos',
            auto: 'private',
            label: 'Mesa Privada'
          }
        }
      }
      return {
        practice: {
          id: 'practice',
          name: 'Mesa Principiante',
          description: 'Práctica libre y entrenamiento',
          auto: 'practice',
          label: 'Práctica Libre'
        },
        quick1: {
          id: 'quick1',
          name: 'Mesa Intermedia',
          description: 'Mesa 1 vs 1 Bola 8 en tiempo real',
          entry: 350,
          payout: 630,
          auto: 'quick1',
          label: '1 vs 1 Bola 8'
        },
        quick4: {
          id: 'quick4',
          name: 'Mesa Avanzada',
          description: 'Mesa para expertos · Partida multijugador',
          entry: 700,
          payout: 2520,
          auto: 'quick4',
          label: 'Torneo 4P'
        },
        private: {
          id: 'private',
          name: 'Mesa Privada',
          description: 'Mesa privada con código de invitación',
          auto: 'private',
          label: 'Mesa Privada'
        }
      }
    },

    isValidJoinCode() {
      return /^[A-Fa-f0-9]{6}$/.test(this.joinCodeInput.trim())
    }
  },
  mounted() {
    this.refreshDisplayedBalance()
    window.addEventListener('message', this.handleWindowMessage)
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handleWindowMessage)
  },
  methods: {
    goToDashboard() {
      this.$router.push('/dashboard')
    },

    async refreshDisplayedBalance() {
      this.isSyncingBalance = true
      try {
        await this.$store.dispatch('games/fetchUserBalance')
      } catch (e) {
        console.error('Error actualizando saldo:', e)
      } finally {
        this.isSyncingBalance = false
      }
    },

    selectTable(table) {
      this.currentModeLabel = table.label
      const glue = this.baseUrl.includes('?') ? '&' : '?'
      this.launchUrl = `${this.baseUrl}${glue}auto=${encodeURIComponent(table.auto)}&t=${Date.now()}`
      this.gameStarted = true
    },

    openPrivateModal() {
      this.privateTab = 'create'
      this.joinCodeInput = ''
      this.showPrivateModal = true
    },

    startPrivateCreate() {
      this.showPrivateModal = false
      this.currentModeLabel = 'Sala Privada (Host)'
      const glue = this.baseUrl.includes('?') ? '&' : '?'
      this.launchUrl = `${this.baseUrl}${glue}auto=create&t=${Date.now()}`
      this.gameStarted = true
    },

    startPrivateJoin() {
      if (!this.isValidJoinCode) return
      const code = this.joinCodeInput.trim().toUpperCase()
      this.showPrivateModal = false
      this.currentModeLabel = `Sala Privada (${code})`
      const glue = this.baseUrl.includes('?') ? '&' : '?'
      this.launchUrl = `${this.baseUrl}${glue}auto=join&code=${encodeURIComponent(code)}&t=${Date.now()}`
      this.gameStarted = true
    },

    exitToLobby() {
      this.gameStarted = false
      this.launchUrl = ''
      this.refreshDisplayedBalance()
    },

    handleWindowMessage(event) {
      if (event.data?.type === 'bolas-exit' || event.data?.type === 'game-exit') {
        this.exitToLobby()
      }
    },

    toggleSound() {
      this.isMuted = !this.isMuted
    }
  }
}
</script>

<style scoped>
.waiting-room {
  min-height: 100vh;
  min-height: 100dvh;
  background-color: #2e0327;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.waiting-room::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(46, 3, 39, 0.88);
  z-index: 0;
  pointer-events: none;
}

.waiting-room.with-nav {
  padding-top: 100px;
  align-items: flex-start;
}

.waiting-room.game-playing {
  padding: 0;
  margin: 0;
}

/* Elementos flotantes decorativos */
.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.casino-chip {
  position: absolute;
  font-size: 2rem;
  opacity: 0.1;
  animation: float 6s ease-in-out infinite;
}

.casino-chip.chip-1 { top: 10%; left: 10%; animation-delay: 0s; }
.casino-chip.chip-2 { top: 20%; right: 15%; animation-delay: 1s; }
.casino-chip.chip-3 { top: 60%; left: 5%; animation-delay: 2s; }
.casino-chip.chip-4 { top: 70%; right: 10%; animation-delay: 3s; }
.casino-chip.chip-5 { top: 40%; left: 20%; animation-delay: 4s; }
.casino-chip.chip-6 { top: 80%; right: 20%; animation-delay: 5s; }

.playing-card {
  position: absolute;
  font-size: 3rem;
  opacity: 0.08;
  animation: rotate 8s linear infinite;
}

.playing-card.card-1 { top: 15%; right: 20%; animation-delay: 0s; }
.playing-card.card-2 { top: 50%; left: 10%; animation-delay: 2s; }
.playing-card.card-3 { top: 75%; right: 30%; animation-delay: 4s; }
.playing-card.card-4 { top: 30%; left: 30%; animation-delay: 6s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Contenedor principal */
.waiting-container {
  background: rgba(33, 11, 23, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 25px;
  padding: 25px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3), 0 0 0 1px hsla(0, 0%, 100%, 0.08);
  max-width: 1280px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 2;
  border: 2px solid rgba(255, 165, 0, 0.15);
}

.waiting-container.game-active {
  max-width: 100%;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  padding: 0;
  margin: 0;
  border-radius: 0;
  border: none;
  background: #000;
}

/* Botón Inicio */
.exit-section-top {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 12px;
}

.exit-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 16px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.exit-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-2px);
}

/* Barra de Saldo */
.waiting-balance-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
  padding: 14px 18px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 200, 39, 0.18) 0%, rgba(255, 107, 53, 0.12) 100%);
  border: 1px solid rgba(255, 200, 39, 0.35);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
}

.waiting-balance-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
  gap: 4px;
}

.waiting-balance-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.85rem;
  font-weight: 600;
}

.waiting-balance-label i {
  color: #ffc827;
}

.waiting-balance-value {
  color: #f5f5f7;
  font-size: 1.65rem;
  font-weight: 800;
  line-height: 1.1;
}

.waiting-balance-value small {
  font-size: 0.95rem;
  font-weight: 700;
  color: #ffc827;
  margin-left: 4px;
}

.waiting-balance-refresh {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.28);
  color: #ffc827;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.waiting-balance-refresh:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.42);
  transform: rotate(20deg);
}

/* Header del Jugador */
.player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid rgba(255, 165, 0, 0.1);
}

.player-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.player-avatar {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 8px 20px rgba(255, 107, 53, 0.3);
}

.player-details h2 {
  margin: 0;
  color: #f5f5f7;
  font-size: 1.8rem;
  font-weight: 800;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(34, 197, 94, 0.1);
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.status-dot {
  width: 8px;
  height: 8px;
  background: #22c55e;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.status-indicator span {
  color: #22c55e;
  font-weight: 600;
  font-size: 0.9rem;
}

/* Títulos y slogan */
.modes-title {
  color: #f5f5f7;
  margin-bottom: 6px;
  font-size: 2.2rem;
  font-weight: 800;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.modes-subtitle {
  color: rgba(245, 245, 247, 0.72);
  margin-bottom: 12px;
  font-size: 1rem;
}

.lobby-hook-wrap {
  margin: 8px 0 25px;
  text-align: center;
}

.lobby-hook {
  margin: 0;
  font-family: 'Pacifico', 'Playfair Display', Georgia, cursive;
  font-size: clamp(1.45rem, 5.2vw, 2.15rem);
  line-height: 2.25;
  letter-spacing: 0.02em;
  background: linear-gradient(90deg, #fff4c4 0%, #fbbf24 42%, #fff8dc 78%, #f59e0b 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 2px 8px rgba(251, 191, 36, 0.35));
}

.lobby-hook em {
  font-style: italic;
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 700;
}

/* Tarjetas de Mesa */
.mode-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;
  margin-bottom: 20px;
}

.mode-card {
  position: relative;
  background: white;
  border-radius: 20px;
  padding: 25px;
  cursor: pointer;
  transition: all 0.4s ease;
  border: 2px solid transparent;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 220px;
}

.mode-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

.mode-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
}

.mode-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  font-size: 2.4rem;
  color: white;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.cpu-mode .mode-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
}

.online-mode .mode-icon {
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
}

.invite-mode .mode-icon {
  background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
}

.mode-content {
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.mode-content h4 {
  color: #1a1a1a;
  font-size: 1.25rem;
  font-weight: 800;
  margin: 0 0 6px;
}

.mode-content p {
  color: #666;
  font-size: 0.9rem;
  margin: 0 0 14px;
}

.mode-pricing {
  display: flex;
  justify-content: space-around;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 10px;
  margin-bottom: 14px;
}

.mode-price {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.price-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #888;
  font-weight: 700;
}

.price-value {
  font-size: 1.15rem;
  font-weight: 800;
  color: #2e0327;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.price-value.free {
  color: #22c55e;
}

.mode-features {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.feature {
  background: #f1f3f5;
  color: #495057;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
}

.mode-action {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-top: 6px;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
}

/* Panel decorativo Dominues */
.mode-accent-panel {
  grid-column: span 2;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  min-height: 200px;
  background: linear-gradient(135deg, #6b1a3d 0%, #4a1229 45%, #2e0327 100%);
  border: 2px solid rgba(255, 165, 0, 0.25);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
}

.accent-content {
  text-align: center;
  padding: 24px;
}

.accent-logo {
  display: block;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: 4px;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  margin-bottom: 6px;
}

.accent-tagline {
  margin: 0 0 14px;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
}

.accent-decor {
  display: flex;
  justify-content: center;
  gap: 18px;
  font-size: 1.5rem;
  opacity: 0.4;
}

.accent-decor span:nth-child(1), .accent-decor span:nth-child(3) { color: #fff; }
.accent-decor span:nth-child(2), .accent-decor span:nth-child(4) { color: #ef4444; }

/* Contenedor del Juego Activo */
.game-container {
  width: 100%;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #000;
}

.game-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #190215;
  border-bottom: 1px solid rgba(255, 165, 0, 0.2);
  z-index: 10;
}

.game-header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.game-name-tag {
  color: #fff;
  font-weight: 800;
  font-size: 1rem;
}

.game-mode-tag {
  background: rgba(255, 165, 0, 0.2);
  color: #ffc827;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
}

.sound-toggle-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.game-viewport {
  flex: 1;
  width: 100%;
  height: calc(100% - 50px);
  position: relative;
}

.game-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

/* Modal Privado */
.private-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.private-modal-content {
  background: #210b17;
  border: 2px solid rgba(255, 165, 0, 0.3);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
  border-radius: 20px;
  max-width: 480px;
  width: 100%;
  padding: 25px;
  color: #fff;
}

.private-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.private-modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #ffc827;
}

.close-modal-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
  cursor: pointer;
}

.private-modal-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin-bottom: 20px;
}

.private-tabs {
  display: flex;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 20px;
}

.tab-btn {
  flex: 1;
  padding: 10px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn.active {
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
}

.tab-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
}

.code-label {
  font-size: 0.8rem;
  font-weight: 800;
  color: #ffc827;
  letter-spacing: 0.5px;
}

.code-input {
  background: rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(255, 165, 0, 0.4);
  color: #fff;
  border-radius: 10px;
  padding: 12px;
  font-size: 1.4rem;
  text-align: center;
  letter-spacing: 4px;
  font-weight: 800;
  text-transform: uppercase;
}

.code-input:focus {
  outline: none;
  border-color: #ff6b35;
}

.action-submit-btn {
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  border: none;
  color: #fff;
  padding: 14px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.4);
  transition: all 0.2s ease;
}

.action-submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 107, 53, 0.6);
}

.action-submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsividad */
@media (max-width: 991px) {
  .mode-options {
    grid-template-columns: 1fr;
  }
  .mode-accent-panel {
    grid-column: span 1;
  }
}
</style>
